import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  MerchantListQuery,
  MerchantRepository,
} from '@app/domain/ports/tpv/merchant.repository';
import {
  AccountMerchantRef,
  TpvMerchant,
  TpvMerchantDetail,
} from '@app/domain/models/tpv/merchant.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import {
  AccountMerchantDto,
  MerchantDetailDto,
  MerchantDto,
} from '@app/data/dtos/tpv/merchant.dto';
import {
  mapAccountMerchant,
  mapMerchant,
  mapMerchantDetail,
} from '@app/data/mappers/tpv/merchant.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';
import { ReadOptions } from '@app/domain/models/read-options.model';
import { backgroundReadContext } from '@app/core/http/silent-background-read.context';

/** `GET /api/v1/tpv/merchants/`. Auth vía interceptores estándar. */
@Injectable()
export class MerchantRemoteDataSource extends MerchantRepository {
  private readonly http = inject(HttpClient);

  override getMerchants(
    query: MerchantListQuery = {},
    options?: ReadOptions,
  ): Observable<TpvPage<TpvMerchant>> {
    // Workaround micro TPV: `get_all=true` devuelve el set completo ya scopeado al
    // usuario. No se envía page/limit (la paginación la arma el micro y deja fuera
    // comercios); los filtros sí siguen aplicando server-side. El conteo real es
    // `data.length`, NO el `total` del body (viene inflado). Ver toTpvPage(getAll).
    const params = toHttpParams({
      get_all: true,
      // Búsqueda server-side (OR entre nombre, razón social, empresa, unidad y
      // RFC). Contrato 2026-09-29: `?search=` sustituye a `?merchant_name=` para
      // la caja de texto — el back miraba solo el nombre y descartaba filas cuyo
      // RFC (o empresa/unidad) coincidía.
      search: query.search,
      // Dual-accept (PK entero u UUID); reemplaza a org_id_company (solo entero).
      id_company: query.idCompany,
      merchant_name: query.merchantName,
      // Arrays → CSV automático en toHttpParams (String(['A','B']) === 'A,B').
      status: query.status,
      entity: query.entity,
      id_unit: query.idUnit,
      start_date: query.startDate,
      end_date: query.endDate,
      // `deleted=true` ⇒ solo eliminados (el backend ignora `status`). Omitido si no aplica.
      deleted: query.deleted ? true : undefined,
    });

    return this.http
      // `context` solo lleva marca cuando el llamador la pidió (feature 152: el
      // contador de comercios de `/products`). La pantalla de Comercios lee esta
      // MISMA url sin ella y su 5xx sigue avisando.
      .get<TpvEnvelope<unknown>>(tpvUrl('merchants/'), { params, context: backgroundReadContext(options) })
      .pipe(
        map((raw) =>
          toTpvPage<MerchantDto, TpvMerchant>(raw, mapMerchant, { page: 1, limit: 0, getAll: true }),
        ),
      );
  }

  override getMerchantsByAccount(polipayAccountId: number): Observable<AccountMerchantRef[]> {
    return this.http
      .get<TpvEnvelope<AccountMerchantDto[]>>(tpvUrl(`accounts/${polipayAccountId}/merchants/`))
      .pipe(
        map((raw) => {
          const list = unwrapTpv<AccountMerchantDto[]>(raw);
          return Array.isArray(list) ? list.map(mapAccountMerchant) : [];
        }),
      );
  }

  override getMerchantById(externalIdMerchant: string): Observable<TpvMerchantDetail> {
    return this.http
      .get<TpvEnvelope<MerchantDetailDto>>(tpvUrl(`merchants/${externalIdMerchant}/`))
      .pipe(map((raw) => mapMerchantDetail(unwrapTpv<MerchantDetailDto>(raw))));
  }
}
